import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING, FONT_SIZE } from '../constants/theme';

/**
 * MetricDetailsModal Component
 *
 * Shows full ranking list for a specific stat category with export to CSV
 *
 * @param {Object} props
 * @param {Object} props.category - The stat category to display
 * @param {Array} props.playerIds - Array of player IDs to include in rankings
 * @param {boolean} props.showLatestOnly - Whether to show latest entry per player
 * @param {Function} props.onClose - Callback to close the modal
 */
const MetricDetailsModal = ({ category, playerIds, showLatestOnly, onClose }) => {
  const { isDesktop } = useResponsive();

  const [allRankings, setAllRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAllRankings();
  }, [category, playerIds, showLatestOnly]);

  const loadAllRankings = async () => {
    try {
      setLoading(true);
      setError('');

      if (showLatestOnly) {
        // Fetch all stats for this category, then filter to latest per player
        const { data: allStats, error } = await supabase
          .from('player_stats')
          .select(
            `
            id,
            value,
            recorded_date,
            player_id,
            player:profiles!player_id(
              id,
              full_name,
              jersey_number,
              team:teams(id, name)
            )
          `
          )
          .eq('stat_category_id', category.id)
          .in('player_id', playerIds)
          .order('recorded_date', { ascending: false });

        if (error) throw error;

        // Group by player and keep only the latest stat per player
        const latestByPlayer = {};
        for (const stat of allStats || []) {
          if (!latestByPlayer[stat.player_id]) {
            latestByPlayer[stat.player_id] = stat;
          }
        }

        // Convert to array and sort by value
        const latestStats = Object.values(latestByPlayer);
        latestStats.sort((a, b) => {
          if (category.sort_direction === 'asc') {
            return a.value - b.value;
          } else {
            return b.value - a.value;
          }
        });

        setAllRankings(latestStats);
      } else {
        // Show all stats (players can appear multiple times)
        const { data, error } = await supabase
          .from('player_stats')
          .select(
            `
            id,
            value,
            recorded_date,
            player:profiles!player_id(
              id,
              full_name,
              jersey_number,
              team:teams(id, name)
            )
          `
          )
          .eq('stat_category_id', category.id)
          .in('player_id', playerIds)
          .order('value', { ascending: category.sort_direction === 'asc' });

        if (error) throw error;
        setAllRankings(data || []);
      }
    } catch (err) {
      console.error('Error loading all rankings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    // Create CSV content
    const headers = showLatestOnly
      ? ['Rank', 'Player', 'Jersey #', 'Team', 'Value', 'Unit']
      : ['Rank', 'Player', 'Jersey #', 'Team', 'Value', 'Unit', 'Date'];

    const rows = allRankings.map((stat, index) => {
      const row = [
        index + 1,
        stat.player?.full_name || 'Unknown',
        stat.player?.jersey_number || '-',
        stat.player?.team?.name || '-',
        stat.value,
        category.unit,
      ];

      if (!showLatestOnly) {
        row.push(new Date(stat.recorded_date).toLocaleDateString());
      }

      return row;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${category.name}_rankings.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getRankColor = (index) => {
    if (index === 0) return '#FFD700'; // Gold
    if (index === 1) return '#C0C0C0'; // Silver
    if (index === 2) return '#CD7F32'; // Bronze
    return 'transparent';
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: SPACING.md,
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: isDesktop ? '800px' : '100%',
      maxHeight: isDesktop ? '80vh' : '90vh',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      padding: SPACING.lg,
      borderBottom: `1px solid ${COLORS.gray[200]}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: SPACING.md,
    },
    title: {
      fontSize: FONT_SIZE.xl,
      fontWeight: '600',
      color: COLORS.gray[900],
      margin: 0,
    },
    subtitle: {
      fontSize: FONT_SIZE.sm,
      color: COLORS.gray[500],
      marginTop: '4px',
    },
    headerButtons: {
      display: 'flex',
      gap: SPACING.sm,
    },
    button: {
      padding: '8px 16px',
      fontSize: FONT_SIZE.sm,
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    exportButton: {
      backgroundColor: COLORS.primary,
      color: 'white',
    },
    closeButton: {
      backgroundColor: COLORS.gray[200],
      color: COLORS.gray[700],
    },
    content: {
      padding: SPACING.lg,
      overflowY: 'auto',
      flex: 1,
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      textAlign: 'left',
      padding: SPACING.md,
      borderBottom: `2px solid ${COLORS.gray[200]}`,
      fontWeight: '600',
      color: COLORS.gray[600],
      fontSize: FONT_SIZE.sm,
      position: 'sticky',
      top: 0,
      backgroundColor: 'white',
      zIndex: 1,
    },
    td: {
      padding: SPACING.md,
      borderBottom: `1px solid ${COLORS.gray[100]}`,
      fontSize: FONT_SIZE.sm,
    },
    rank: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: FONT_SIZE.sm,
      fontWeight: '600',
    },
    playerName: {
      fontWeight: '500',
      color: COLORS.gray[900],
    },
    statValue: {
      fontWeight: '600',
      color: COLORS.gray[900],
    },
    loading: {
      textAlign: 'center',
      padding: SPACING.xl,
      color: COLORS.gray[500],
    },
    error: {
      padding: SPACING.md,
      backgroundColor: COLORS.alert.error.bg,
      color: COLORS.alert.error.text,
      border: `1px solid ${COLORS.alert.error.border}`,
      borderRadius: '4px',
      marginBottom: SPACING.md,
    },
    emptyState: {
      textAlign: 'center',
      padding: SPACING.xl,
      color: COLORS.gray[500],
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{category.name}</h2>
            <div style={styles.subtitle}>
              {category.sort_direction === 'desc' ? '↑ Higher is Better' : '↓ Lower is Better'} •
              {showLatestOnly ? ' Latest Entry Per Player' : ' All Stats Recorded'}
            </div>
          </div>
          <div style={styles.headerButtons}>
            <button
              style={{ ...styles.button, ...styles.exportButton }}
              onClick={handleExportCSV}
              disabled={loading || allRankings.length === 0}
            >
              Export CSV
            </button>
            <button style={{ ...styles.button, ...styles.closeButton }} onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div style={styles.content}>
          {error && <div style={styles.error}>{error}</div>}

          {loading ? (
            <div style={styles.loading}>Loading rankings...</div>
          ) : allRankings.length === 0 ? (
            <div style={styles.emptyState}>No stats recorded for this metric</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Rank</th>
                  <th style={styles.th}>Player</th>
                  <th style={styles.th}>Jersey</th>
                  <th style={styles.th}>Team</th>
                  <th style={styles.th}>Value</th>
                  {!showLatestOnly && <th style={styles.th}>Date</th>}
                </tr>
              </thead>
              <tbody>
                {allRankings.map((stat, index) => (
                  <tr key={stat.id}>
                    <td style={styles.td}>
                      <div
                        style={{
                          ...styles.rank,
                          backgroundColor: getRankColor(index),
                          color: index < 3 ? 'white' : COLORS.gray[700],
                          border: index < 3 ? 'none' : `2px solid ${COLORS.gray[300]}`,
                        }}
                      >
                        {index + 1}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.playerName}>
                        {stat.player?.full_name || 'Unknown Player'}
                      </span>
                    </td>
                    <td style={styles.td}>{stat.player?.jersey_number || '-'}</td>
                    <td style={styles.td}>{stat.player?.team?.name || '-'}</td>
                    <td style={styles.td}>
                      <span style={styles.statValue}>
                        {stat.value} {category.unit}
                      </span>
                    </td>
                    {!showLatestOnly && (
                      <td style={styles.td}>{new Date(stat.recorded_date).toLocaleDateString()}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricDetailsModal;
